import {gql} from '@apollo/client';
import {Col, Row, Space, notification, Skeleton} from 'antd';
import {BigNumber} from 'ethers';
import {find} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import {
  Artist,
  BusyButton,
  MintReveal,
  Sparkles,
  TransactionButton,
  WaitButton,
} from 'src/components';
import {MintType} from 'src/components/MintReveal';
import {useAddress, useGameContract, useMonaTokensContract, useSubscription} from 'src/hooks';
import {useLisaMonaParameters} from 'src/hooks/useLisaMonaParameters';
import {PlayerArtists} from './__generated__/PlayerArtists';
import styles from './index.module.scss';

const GQL_MY_ARTISTS = gql`
  subscription PlayerArtists($playerId: ID!) {
    player(subgraphError: allow, id: $playerId) {
      id
      artists(orderBy: mintedAt, orderDirection: desc) {
        id
        uri
        name
        mintedAt
        description
        imageUri
        creationReadyAt
      }
    }
  }
`;

function notifyMintArtistFail(description: string) {
  notification.error({
    message: 'Unable to mint artist',
    description,
    duration: 8,
  });
}

export default function Home() {
  const history = useHistory();
  const contract = useGameContract();
  const monaTokens = useMonaTokensContract();
  const [unveilArtId, setUnveilArtId] = useState<string>();
  const [unveilArtistId, setUnveilArtistId] = useState<string>();
  const [ethMintArtist, setEthMintArtist] = useState<BigNumber>();
  const {artEth} = useLisaMonaParameters();
  const address = useAddress();
  const {data, loading} = useSubscription<PlayerArtists>(GQL_MY_ARTISTS, {
    variables: {
      playerId: address,
    },
  });

  useEffect(() => {
    async function fetchPrice() {
      try {
        setEthMintArtist(await contract.getPriceEthMintArtist());
      } catch (err) {
        console.error(err);
        notification.error({
          message: 'Unable to reach contract',
          description: 'Try refreshing the page',
        });
      }
    }
    fetchPrice();
  }, [contract]);

  const getArtist = useCallback(
    function getArtist(id: string) {
      return find(data?.player?.artists, {id});
    },
    [data],
  );

  const _unveilArtistMint = useCallback(
    (receipt) => {
      const mintEvent = find(receipt.events, {event: 'MintArtist'});
      const id = mintEvent.decode(mintEvent.data)?.id;
      setUnveilArtistId(id);
    },
    [setUnveilArtistId],
  );

  const handleRefreshMetadata = useCallback(
    (id: string) => {
      return monaTokens.refreshMetadata([id]);
    },
    [monaTokens],
  );

  const handleMintArtistEth = useCallback(
    (eth) => contract.mintArtistWithEth({value: eth}),
    [contract],
  );

  const createStartArtHandler = useCallback(
    (id: string) => (eth?: BigNumber) => contract.startArtWithEth(id, {value: eth}),
    [contract],
  );

  const createMintArtHandler = useCallback(
    (id: string) => () => contract.mintArtistCreation(id),
    [contract],
  );

  const artists = data?.player?.artists;
  const noArtists = address && data && (!artists || artists.length === 0);

  return (
    <>
      <Row align='middle'>
        <Col span={24}>
          <div className={styles.headerBar}>
            {ethMintArtist && (
              <TransactionButton
                type='primary'
                size='large'
                eth={ethMintArtist}
                sparkling={noArtists}
                onRunTransaction={handleMintArtistEth}
                onReceipt={_unveilArtistMint}
                onFail={(_err, message) => notifyMintArtistFail(message)}>
                Mint Artist
              </TransactionButton>
            )}
          </div>
        </Col>
      </Row>
      <br />

      <h1>{noArtists ? 'You do not have any artists, try minting one to start!' : 'My Artists'}</h1>
      <Skeleton loading={loading || !address} active={loading}>
        <Row gutter={[16, 16]}>
          {data && (
            <TransitionGroup appear={true} component={null}>
              {artists?.map((a) => {
                const creationReadyAt = new Date(a.creationReadyAt * 1000);
                const isBusy = creationReadyAt > new Date(0);
                return (
                  <CSSTransition
                    key={a.id}
                    component={null}
                    classNames={{
                      enterActive: 'animate__animated animate__lightSpeedInLeft',
                      exitActive: 'animate__animated animate__lightSpeedOutRight',
                    }}
                    timeout={900}>
                    <Col sm={24} md={12} lg={8}>
                      <Artist {...a}>
                        <br />
                        <Row justify='end'>
                          <Space>
                            {!a.imageUri && (
                              <BusyButton onClick={() => handleRefreshMetadata(a.id)}>
                                ✨
                              </BusyButton>
                            )}
                            {!isBusy && artEth && (
                              <TransactionButton
                                eth={artEth}
                                onRunTransaction={createStartArtHandler(a.id)}
                                onReceipt={() => {
                                  notification.success({
                                    message: 'New art started',
                                    description: `${
                                      getArtist(a.id)?.name
                                    } has started a new creation!`,
                                  });
                                }}
                                onFail={(_err, message) => {
                                  notification.error({
                                    message: 'Unable to start new art',
                                    description: message,
                                  });
                                }}
                                type='default'
                                size='small'>
                                Start Art
                              </TransactionButton>
                            )}

                            {isBusy && (
                              <TransactionButton
                                ButtonComponent={WaitButton}
                                onRunTransaction={createMintArtHandler(a.id)}
                                onReceipt={(receipt) => {
                                  const mintEvent = find(receipt.events, {
                                    event: 'MintArtistCreation',
                                  });
                                  if (mintEvent) {
                                    //@ts-ignore
                                    const artId = mintEvent.decode(mintEvent.data)?.id;
                                    setUnveilArtId(artId);
                                  }
                                }}
                                onFail={(_err, message) => {
                                  notification.error({
                                    message: 'Unable to mint art',
                                    description: message,
                                  });
                                }}
                                readyAt={creationReadyAt}
                                type='primary'
                                size='small'>
                                {(ready, timeLeft) =>
                                  ready ? (
                                    <Sparkles>Ready to Mint Art!</Sparkles>
                                  ) : (
                                    `Mint ready in ${timeLeft}`
                                  )
                                }
                              </TransactionButton>
                            )}
                          </Space>
                        </Row>
                      </Artist>
                    </Col>
                  </CSSTransition>
                );
              })}
            </TransitionGroup>
          )}
        </Row>
      </Skeleton>
      {unveilArtId && (
        <MintReveal
          mintType={MintType.Art}
          id={unveilArtId}
          onClose={() => {
            setUnveilArtId(undefined);
            history.push('/me/arts');
          }}
        />
      )}
      {unveilArtistId && (
        <MintReveal
          mintType={MintType.Artist}
          id={unveilArtistId}
          onClose={() => setUnveilArtistId(undefined)}
        />
      )}
    </>
  );
}
